import { DataTypeOption, DataTypeAPIOptions } from '../components/fields/FieldsSettings/FieldsSettingsTypes';

/**
 * Function to get the default data format configuration based on the data type and available options.
 * This function is essential for setting up the initial state for fields that have specific data formatting requirements.
 *
 * @param {string} [dataType] - The data type for which the default format is needed. E.g., 'date', 'amount', 'number'.
 * @param {string|Object|null} [defaultConfig] - The default configuration provided for the data type.
 *                                        This will usually come from a constants file or user settings.
 * @param {Object} [dataTypeOptions] - The format options available for the data type.
 *                                     These options typically come from an API response.
 *
 * @returns {Object} - Returns an object containing the default format configuration for the given data type,
 *                     or an empty object if the data type or options are not provided.
 */
export const getDefaultDataFormat = (
  dataType?: string,
  defaultConfig?: string | { decimalSymbol?: string; thousandsGrouping?: string } | null,
  dataTypeOptions?: DataTypeAPIOptions
): DataTypeOption => {
  // Return an empty object if the dataType is not provided
  if (!dataType) return {};

  // Handling for 'date' data type
  if (dataType === 'date' && dataTypeOptions?.order) {
    // If a default config is provided and it's a string, use that. Otherwise, use the first available option.
    const orderValue = typeof defaultConfig === 'string' ? defaultConfig : dataTypeOptions.order[0];
    return { order: orderValue };
  }

  // Handling for 'amount' and 'number' data types
  if (['amount', 'number'].includes(dataType) && dataTypeOptions?.decimalSymbol) {
    const config: { decimalSymbol: string; thousandsGrouping?: string } = {
      decimalSymbol:
        typeof defaultConfig === 'object'
          ? defaultConfig?.decimalSymbol || dataTypeOptions.decimalSymbol[0]
          : (defaultConfig as string) || dataTypeOptions.decimalSymbol[0],
    };

    // Add thousandsGrouping if available in options
    if (dataTypeOptions?.thousandsGrouping) {
      config.thousandsGrouping =
        typeof defaultConfig === 'object'
          ? defaultConfig?.thousandsGrouping || dataTypeOptions.thousandsGrouping[0]
          : dataTypeOptions.thousandsGrouping[0];
    }

    return config;
  }

  // Return an empty object if no match
  return {};
};
